import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Loader2, ShieldCheck, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

export default function AutopilotReviewPanel() {
  const [rawText, setRawText] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async (dryRun) => {
    const text = rawText.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/mod/autopilot/ingest`, {
        raw_text: text,
        facebook_url: facebookUrl.trim() || null,
        dry_run: dryRun,
      }, { headers: modHeaders() });
      setResult(data);
      if (dryRun) {
        toast.info("Analysen är klar – inget sparades.");
      } else if (data?.queue?.duplicate) {
        toast.info("Signalen fanns redan i granskningskön.");
      } else {
        toast.success("Analysen är sparad för granskning.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Autopilot-fel.");
    } finally {
      setBusy(false);
    }
  };

  const r = result?.result;

  return (
    <section className="bg-parchment border border-pine/40 rounded-sm p-4 mb-3" data-testid="autopilot-review-panel">
      <div className="flex items-start gap-2 mb-3">
        <ShieldCheck size={18} className="text-pine mt-0.5" />
        <div>
          <h3 className="font-heading text-base font-medium text-ink">Autopilot – granskning först</h3>
          <p className="text-xs text-bark mt-1">Klistra in en observerad text. Systemet klassificerar den, men publicerar eller avbokar inget automatiskt.</p>
        </div>
      </div>

      <textarea
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        rows={5}
        placeholder="Klistra in Facebook-inlägg eller annan observerad text…"
        className="w-full text-sm px-3 py-2 border border-warm-border rounded-sm bg-white mb-2"
        data-testid="autopilot-review-text"
      />
      <input
        value={facebookUrl}
        onChange={(e) => setFacebookUrl(e.target.value)}
        placeholder="Facebook-URL för spårbarhet (valfri)"
        className="w-full text-sm px-3 py-2 border border-warm-border rounded-sm bg-white mb-2"
      />
      {error && <p className="text-xs text-red-700 mb-2">{String(error)}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => run(false)} disabled={busy || !rawText.trim()} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-pine text-cream border border-pine rounded-sm disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Analysera och lägg i granskning
        </button>
        <button type="button" onClick={() => run(true)} disabled={busy || !rawText.trim()} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-white text-bark border border-warm-border rounded-sm disabled:opacity-50">
          <Eye size={12} /> Testa utan att spara
        </button>
      </div>

      {r && (
        <div className="mt-3 pt-3 border-t border-warm-border text-xs" data-testid="autopilot-review-result">
          <p><span className="text-bark">Bedömning:</span> <strong className="text-ink">{r.target_area || "discarded"}</strong> · {Math.round((r.confidence || 0) * 100)} %</p>
          {r.titel && <p className="mt-1"><span className="text-bark">Titel:</span> {r.titel}{r.datum ? ` · ${r.datum}` : ""}</p>}
          {r.reasoning && <p className="mt-1 text-bark italic">{r.reasoning}</p>}
          {!result.dry_run && <p className="mt-2 text-pine font-medium">Sparad som signal för manuell granskning – inte publicerad.</p>}
        </div>
      )}
    </section>
  );
}
